import {
  activePanelTab,
  closePanelTab,
  openPanelTab,
  type PanelTabs,
  showPanelView,
} from '@/lib/side-panel/panel-tabs';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';
import { emptyPanelLayout, type PanelLayout } from './panel-layouts';
import { panelSplitWithTabs } from './panel-split';
import type { AppPanelTarget, DesktopShellStore } from './store';

/** The side panel takes the whole workspace: the user maximized it (the conversation floats). */
export function panelMaximized(state: { sidePanelOpen: boolean; panelSize: number }): boolean {
  return state.sidePanelOpen && state.panelSize >= 100;
}

/** Maximizes the side panel, remembering its size to restore (the user's "Maximize panel"). */
export function maximizePanel(store: DesktopShellStore): void {
  const state = store.getState();
  if (state.panelSize < 100) state.setPanelRestoreSize(state.panelSize);
  state.setPanelSize(100);
}

/** Gives the side panel back the size it had before it was maximized. */
export function restorePanel(store: DesktopShellStore): void {
  const state = store.getState();
  if (state.panelSize >= 100) state.setPanelSize(state.panelRestoreSize);
}

/** How long the conversation the shown tab is carried to is awaited. */
const CARRY_TAB_MS = 15_000;

/**
 * Carries the shown panel's tab (a fullscreen view the floating composer writes about) to `to`
 * once it is shown, maximized there, with what the tab shows (a message's diff for Review); the
 * conversation it leaves keeps the rest of its panel. `null` carries it to the next conversation
 * shown, whichever it is (a new one, shown at once). Run it right before navigating; nothing
 * moves when another conversation is shown first, none is in time, or the returned cancel runs.
 */
export function carryShownTabTo(store: DesktopShellStore, to: string | null): () => void {
  const before = store.getState();
  const tab = activePanelTab(before.panelTabs);
  if (tab === null) return () => undefined;
  const from = before.chatId;
  const target = before.appPanelTargets[tab.id];
  const diff = tab.view === 'review' ? before.messageReviewDiff : null;
  const cancel = () => {
    stop();
    clearTimeout(timer);
  };
  const stop = store.subscribe((state) => {
    if (state.chatId === from) return;
    cancel();
    if (to !== null && state.chatId !== to) return;
    const left = state.panelLayouts[from];
    const tabs = openPanelTab(state.panelTabs, tab);
    store.setState({
      sidePanelOpen: true,
      panelTabs: tabs,
      panelSize: 100,
      panelRestoreSize: state.panelSize >= 100 ? state.panelRestoreSize : state.panelSize,
      appPanelTargets:
        target === undefined
          ? state.appPanelTargets
          : { ...state.appPanelTargets, [tab.id]: target },
      panelSplit: null,
      ...(diff === null ? {} : { messageReviewDiff: diff }),
      ...(left === undefined
        ? {}
        : {
            panelLayouts: {
              ...state.panelLayouts,
              [from]: carriedAway(left, tab.id, diff !== null),
            },
          }),
    });
  });
  const timer = setTimeout(stop, CARRY_TAB_MS);
  return cancel;
}

/** `layout` without the tab carried to another conversation. */
function carriedAway(layout: PanelLayout, tabId: string, diffCarried: boolean): PanelLayout {
  const tabs = closePanelTab(layout.tabs, tabId);
  const { [tabId]: _carried, ...appTargets } = layout.appTargets;
  return {
    ...layout,
    open: layout.open && tabs.tabs.length > 0,
    tabs,
    size: layout.size >= 100 ? layout.restoreSize : layout.size,
    appTargets,
    messageReviewDiff: diffCarried ? null : layout.messageReviewDiff,
    split: panelSplitWithTabs(layout.split, tabs),
  };
}

/**
 * Docks an app in the conversation on screen: its tab in the side panel, showing `target` when
 * given (an item, a route of its view). It never maximizes the panel nor folds the sidebar: from
 * a conversation an app is only a tab beside it (docs/redesign-implementation-plan.md §6.1).
 */
export function dockApp(
  store: DesktopShellStore,
  view: SidePanelView,
  target?: AppPanelTarget,
): void {
  const state = store.getState();
  const tabs = showPanelView(state.panelTabs, view);
  state.setPanelTabs(tabs);
  const tab = activePanelTab(tabs);
  if (target !== undefined && tab !== null) setAppPanelTarget(store, tab.id, target);
  state.setSidePanelOpen(true);
}

/**
 * Docks an app in `conversation`'s side panel: the one on screen shows it at once; another one
 * gets the tab ready in its panel without taking the screen ("prepared"), shown when the user
 * opens that conversation.
 */
export function dockAppIn(
  store: DesktopShellStore,
  conversation: string,
  view: SidePanelView,
  target?: AppPanelTarget,
): 'shown' | 'prepared' {
  const state = store.getState();
  if (conversation === state.chatId) {
    dockApp(store, view, target);
    return 'shown';
  }
  const width = state.panelSize >= 100 ? state.panelRestoreSize : state.panelSize;
  const layout: PanelLayout = state.panelLayouts[conversation] ?? emptyPanelLayout(width);
  const tabs = showPanelView(layout.tabs, view);
  const tab = activePanelTab(tabs);
  state.setPanelLayouts({
    ...state.panelLayouts,
    [conversation]: {
      ...layout,
      open: true,
      tabs,
      appTargets:
        target === undefined || tab === null
          ? layout.appTargets
          : { ...layout.appTargets, [tab.id]: target },
    },
  });
  return 'prepared';
}

/** Closes the app tab `tabId` (its frame's Close); the panel closes with its last tab. */
export function closeAppPanelTab(store: DesktopShellStore, tabId: string): void {
  const state = store.getState();
  const tabs = closePanelTab(state.panelTabs, tabId);
  state.setPanelTabs(tabs);
  if (tabs.tabs.length === 0) state.setSidePanelOpen(false);
}

/** What the app tab `tabId` shows from now on. */
export function setAppPanelTarget(
  store: DesktopShellStore,
  tabId: string,
  target: AppPanelTarget,
): void {
  store.getState().setAppPanelTargets((current) => ({ ...current, [tabId]: target }));
}

/**
 * Opens another tab of an app on `target` (an item dragged from its sidebar), without showing it:
 * the split it is dropped in shows it. Returns its id.
 */
export function addAppPanelTab(
  store: DesktopShellStore,
  view: SidePanelView,
  target: AppPanelTarget,
): string {
  const id = crypto.randomUUID();
  const state = store.getState();
  state.setPanelTabs((tabs) => ({ ...openPanelTab(tabs, { id, view }), activeId: tabs.activeId }));
  setAppPanelTarget(store, id, target);
  return id;
}

/** The app tabs' targets, without those of the tabs no longer open. */
export function appTargetsWithTabs(
  targets: Readonly<Record<string, AppPanelTarget>>,
  tabs: PanelTabs,
): Readonly<Record<string, AppPanelTarget>> {
  const open = Object.entries(targets).filter(([id]) => tabs.tabs.some((tab) => tab.id === id));
  return open.length === Object.keys(targets).length ? targets : Object.fromEntries(open);
}
